/**
 * RegionField — champ « Région » éditable au clic avec bulle de propositions basée sur
 * l'index des appellations (vignobles + sous-régions connus). Réutilisable partout
 * (fiche bouteille, fiche domaine, dégustation…). Aucun doublon de logique ailleurs.
 */
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Check } from 'lucide-react';
import { vignoblesApi } from '../../services/api';

const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function RegionField({ value, onSave, testId = 'region', inputClassName = '', displayClassName = '' }) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => setQ(value ?? ''), [value]);

  const { data: appellations = [] } = useQuery({
    queryKey: ['appellations', ''], queryFn: () => vignoblesApi.appellations(''), enabled: editing,
  });
  const regions = useMemo(() => {
    const set = new Set();
    appellations.forEach((a) => { if (a.vignoble) set.add(a.vignoble); if (a.sous_region) set.add(a.sous_region); });
    return [...set].sort();
  }, [appellations]);
  const matches = useMemo(() => {
    const t = norm(q);
    return regions.filter((r) => (!t ? true : norm(r).includes(t))).slice(0, 12);
  }, [regions, q]);

  const commit = async (v) => {
    setEditing(false); setOpen(false);
    if ((v ?? '') !== (value ?? '')) { await onSave(v); setSaved(true); setTimeout(() => setSaved(false), 1500); }
  };

  if (!editing) {
    return (
      <span role="button" tabIndex={0} data-testid={`${testId}-display`}
        onClick={() => { setEditing(true); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(true); setOpen(true); } }}
        className={`group inline-flex items-start gap-1 cursor-text rounded px-0.5 hover:outline hover:outline-1 hover:outline-dashed hover:outline-gold ${displayClassName}`}>
        <span className={!value ? 'text-n500' : ''}>{value || 'Région'}</span>
        <Pencil className="w-3 h-3 mt-1 shrink-0 opacity-0 group-hover:opacity-60 text-gold700" />
        {saved && <Check className="w-3 h-3 mt-1 text-gold700" />}
      </span>
    );
  }
  return (
    <span className="relative inline-block">
      <input autoFocus value={q} className={`vd-input ${inputClassName}`} data-testid={`${testId}-input`}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => commit(q), 160)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(q); if (e.key === 'Escape') { setQ(value ?? ''); setEditing(false); setOpen(false); } }} />
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 bg-paper border border-hair rounded shadow-md w-64 max-h-56 overflow-auto" data-testid={`${testId}-menu`}>
          {matches.map((r) => (
            <button key={r} data-testid={`${testId}-opt-${r}`} onMouseDown={() => commit(r)}
              className="block w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-surface">{r}</button>
          ))}
        </div>
      )}
    </span>
  );
}
